// A player's season in one league, week by week. PLAN-2026-09-24 item H.
//
// A manager in week three wants to see how their players did in weeks one
// and two. So the weeks BEFORE a league's first week are here, labeled as
// such — a league that starts in week 3 still drafted players who played
// weeks 1 and 2, and those games are the evidence a manager has.
//
// SCORED THIS LEAGUE'S WAY. The points are the league's own scoring over the
// stored line, by the same `packages/scoring` the matchups use; a half-PPR
// league reads half-PPR numbers for weeks it never played.
//
// A WEEK WITH NO LINE IS NOT A ZERO. A bye, a game the player had no stats in,
// a game not played yet and a game whose line has not landed are four
// different sentences, and every one of them is a state here rather than a
// `0` a screen would print as a bad week.

import { MatchupCue } from './matchup-cue';
import { z } from 'zod';
import { PlayerSummary, ScoreItem, TeamRef } from './views';

export const PlayerWeekState = z.enum([
  /** A stat line: `points` and `statLine` are set. */
  'played',
  /** The player's club had no game that week. */
  'bye',
  /** The club's game is final and the player has no line — inactive, or no stats. */
  'no-line',
  /** The club's game has kicked off and no line has landed yet. */
  'pending',
  /** The club's game has not kicked off. */
  'upcoming',
]);
export type PlayerWeekState = z.infer<typeof PlayerWeekState>;

export const PlayerWeekRow = z.object({
  /** The period's index in the sport's calendar: 1 for Week 1. */
  period: z.number().int().min(1),
  label: z.string(),
  windowId: z.string(),
  /** A week before this league's first regular week: played, and not this league's season. */
  beforeSeason: z.boolean(),
  state: PlayerWeekState,
  /** The club faced, when there was a game. */
  opponent: z.string().nullable(),
  home: z.boolean().nullable(),
  /** The game's kickoff, when there was one. */
  kickoff: z.string().nullable(),
  /** Under this league's scoring, rounded as it rounds. Null unless `played`. */
  points: z.number().nullable(),
  /** The sport's one-line summary of the line, e.g. `18/27, 241 yds, 2 TD`. Null unless `played`. */
  statLine: z.string().nullable(),
  /**
   * The week's numbers for the game log, keyed by column: every column of
   * `PlayerWeeksView.columns` there is a number for, as a list's row holds
   * them (`RowStatsView.values`), a made-of column's tries under its
   * `attempts` key. Null unless `played`. Added 2026-09-27 (B-218).
   */
  stats: z.record(z.string(), z.number()).nullable(),
  /**
   * How the week's points were made, stat by stat, under the rules the week
   * was scored under: each scoring stat's value and the points it earned, a
   * bonus as its own item. Items worth nothing are left out. Null unless
   * `played`. Added 2026-09-27 (B-218).
   */
  breakdown: z.array(ScoreItem).nullable(),
});
export type PlayerWeekRow = z.infer<typeof PlayerWeekRow>;

/**
 * A week still to come in the player's season (B-218): his club's game, or a
 * bye. From the current week's game if it has not kicked off, to the end of
 * the calendar.
 */
export const UpcomingWeek = z.object({
  period: z.number().int().min(1),
  label: z.string(),
  windowId: z.string(),
  state: z.enum(['game', 'bye']),
  opponent: z.string().nullable(),
  home: z.boolean().nullable(),
  kickoff: z.string().nullable(),
  /**
   * How the opponent has done against the player's position this season, in
   * this league's scoring: fantasy points a game allowed to that position,
   * where that ranks (1 = the most allowed, of `of` clubs), and how many
   * games it rests on. Null for a bye, or before the opponent has played.
   */
  versus: MatchupCue.nullable(),
});
export type UpcomingWeek = z.infer<typeof UpcomingWeek>;

/**
 * A game-log column: its key, its short label and its full name, and as a
 * list's column has them (`StatColumnView`, B-517) the least detail level
 * it shows at, its places and a made-of column's attempts key. `level`,
 * `decimals` and `attempts` added 2026-10-26.
 */
export const LogColumn = z.object({
  key: z.string(),
  label: z.string(),
  name: z.string(),
  level: z.enum(['simple', 'standard', 'everything']).default('standard'),
  decimals: z.number().int().min(0).max(3).default(0),
  attempts: z.string().nullable().default(null),
});
export type LogColumn = z.infer<typeof LogColumn>;

export const PlayerWeeksView = z.object({
  leagueId: z.string(),
  season: z.number().int(),
  player: PlayerSummary,
  /** The team in this league that holds the player; null for one nobody holds. */
  heldBy: TeamRef.nullable(),
  /** The league's first regular week. Rows before it are `beforeSeason`. */
  firstPeriod: z.number().int().min(1),
  /** Over the `played` rows: how many, their points, and points a game. */
  summary: z.object({
    games: z.number().int().min(0),
    points: z.number(),
    perGame: z.number().nullable(),
    /**
     * Each of `columns` over the same rows, keyed by column, as a list's row
     * reads a range (a rate over the season's sums, not an average of the
     * weeks') — a tiered stat no row reported left out, as a row leaves it —
     * for the log's totals row. Added 2026-10-18 (B-414); absent is none.
     */
    totals: z.record(z.string(), z.number()).default({}),
  }),
  /** One row per week of the season that has started, oldest first. */
  weeks: z.array(PlayerWeekRow),
  /**
   * The game log's columns: his position's stat sheet (B-517), whatever his
   * numbers, so a quarterback's log has no receiving columns after a catch
   * and a receiver's none for a trick-play pass (since 2026-10-26; before,
   * the columns a played week had a number in), without a share of his
   * club's total, which no week keeps. Added 2026-09-27 (B-218).
   */
  columns: z.array(LogColumn),
  /** The rest of his season, week by week (B-218). */
  upcoming: z.array(UpcomingWeek),
  asOf: z.string(),
});
export type PlayerWeeksView = z.infer<typeof PlayerWeeksView>;

/**
 * His last season, in this league's scoring now (B-414): his games, points
 * and points a game, and the game log's columns (his position's stat sheet,
 * B-517) with each over the season — a tiered stat no game reported left
 * out. The current version of each real line. Added 2026-10-18; the columns
 * he had a number in until 2026-10-26.
 */
export const LastSeasonView = z.object({
  season: z.number().int(),
  games: z.number().int().min(1),
  points: z.number(),
  perGame: z.number(),
  totals: z.record(z.string(), z.number()),
  columns: z.array(LogColumn),
});
export type LastSeasonView = z.infer<typeof LastSeasonView>;
